import type { Properties } from "posthog-js";

type Consent = {
  necessary?: boolean;
  analytics?: boolean;
  marketing?: boolean;
};

const posthogKey = import.meta.env.VITE_POSTHOG_KEY;
const posthogHost = import.meta.env.VITE_POSTHOG_HOST;

let client: typeof import("posthog-js") | null = null;
let loadingPromise: Promise<typeof import("posthog-js") | null> | null = null;

function readLocalConsent(): Consent | null {
  try {
    const raw = localStorage.getItem("cookie_consent");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

async function ensureLoaded(consentOverride?: Consent) {
  if (!posthogKey || !posthogHost) return null;

  const consent = consentOverride ?? readLocalConsent();
  const analyticsAllowed = !!consent?.analytics;
  if (!analyticsAllowed) return null;

  if (client) return client;
  if (loadingPromise) return loadingPromise;

  loadingPromise = import("posthog-js")
    .then(({ default: posthog }) => {
      posthog.init(posthogKey, {
        api_host: posthogHost,
        autocapture: true,
        capture_pageview: true,
        capture_pageleave: true,
      });
      client = posthog;
      return client;
    })
    .catch((err) => {
      console.error("PostHog load failed", err);
      loadingPromise = null;
      client = null;
      return null;
    });

  return loadingPromise;
}

export function initAnalytics(consentOverride?: Consent) {
  void ensureLoaded(consentOverride);
}

export function captureEvent(event: string, properties?: Properties) {
  if (!client) {
    ensureLoaded().then((ph) => ph?.capture(event, properties));
    return;
  }
  client.capture(event, properties);
}

export function identifyUser(distinctId: string, properties?: Properties) {
  ensureLoaded().then((ph) => ph?.identify(distinctId, properties));
}

export function resetAnalytics() {
  if (!client) return;
  client.reset();
}
